
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DateRange } from '@/components/common/DateRangePicker';

interface AnalyticsFilters {
  selectedProjectId?: string | "all";
  showBillableOnly?: boolean;
  showTeamProjectsOnly?: boolean;
  dateRange?: DateRange;
}

export function useAnalyticsData(filters: AnalyticsFilters = {}) {
  const { selectedProjectId = "all", showBillableOnly = false, showTeamProjectsOnly = false, dateRange } = filters;

  const { data: timeEntries = [], isLoading: isLoadingTimeEntries } = useQuery({
    queryKey: ['analytics-time-entries', selectedProjectId, dateRange],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      // Fetch personal time entries
      let personalQuery = supabase
        .from('time_entries')
        .select('*')
        .order('date', { ascending: false });

      // Apply date range filter for personal entries
      if (dateRange?.from && dateRange?.to) {
        personalQuery = personalQuery
          .gte('date', dateRange.from.toISOString())
          .lte('date', dateRange.to.toISOString());
      }

      // Apply project filter for personal entries
      if (selectedProjectId !== "all") {
        personalQuery = personalQuery.eq('project_id', selectedProjectId);
      }

      // Fetch team time entries with user info
      let teamQuery = supabase
        .from('team_time_entries')
        .select(`
          *,
          team_projects!inner(
            id,
            name,
            hourly_rate,
            currency,
            created_by
          )
        `)
        .order('date', { ascending: false });

      // Apply date range filter for team entries
      if (dateRange?.from && dateRange?.to) {
        teamQuery = teamQuery
          .gte('date', dateRange.from.toISOString())
          .lte('date', dateRange.to.toISOString());
      }

      // Apply project filter for team entries
      if (selectedProjectId !== "all") {
        teamQuery = teamQuery.eq('project_id', selectedProjectId);
      }

      const [personalResult, teamResult] = await Promise.all([
        personalQuery,
        teamQuery
      ]);

      if (personalResult.error) throw personalResult.error;
      if (teamResult.error) throw teamResult.error;

      // Normalize personal entries - respect is_billable column
      const personalEntries = (personalResult.data || []).map(entry => ({
        id: entry.id,
        date: new Date(entry.date),
        minutes: entry.minutes,
        projectId: entry.project_id,
        description: entry.description,
        isBillable: entry.is_billable ?? true, // Use database value or default to true
        isTeamEntry: false,
        hourlyRate: null, // Will be filled from project data
        teamProjectName: null,
        userName: null
      }));

      // Normalize team entries
      const teamEntries = (teamResult.data || []).map(entry => ({
        id: entry.id,
        date: new Date(entry.date),
        minutes: entry.minutes,
        projectId: entry.project_id,
        description: entry.description,
        isBillable: entry.is_billable,
        isTeamEntry: true,
        hourlyRate: entry.hourly_rate || entry.team_projects?.hourly_rate || null,
        teamProjectName: entry.team_projects?.name || null,
        userName: 'Team Member'
      }));

      return [...personalEntries, ...teamEntries].sort((a, b) => b.date.getTime() - a.date.getTime());
    }
  });

  const { data: projects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: ['analytics-projects'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      // Fetch personal projects
      const personalProjectsResult = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      // Fetch team projects
      const teamProjectsResult = await supabase
        .from('team_projects')
        .select(`
          *,
          team_project_members!inner(
            user_id,
            role
          )
        `)
        .eq('team_project_members.user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (personalProjectsResult.error) throw personalProjectsResult.error;
      if (teamProjectsResult.error) throw teamProjectsResult.error;

      // Normalize personal projects
      const personalProjects = (personalProjectsResult.data || []).map(project => ({
        id: project.id,
        name: project.name,
        description: project.description || undefined,
        isArchived: project.is_archived,
        createdAt: new Date(project.created_at),
        hourlyRate: Number(project.hourly_rate),
        user_id: project.user_id,
        team_id: project.team_id,
        owner_id: project.owner_id,
        is_team_project: project.is_team_project || false,
        
        isTeamProject: false
      }));

      // Normalize team projects
      const teamProjects = (teamProjectsResult.data || []).map(project => ({
        id: project.id,
        name: project.name,
        description: project.description || undefined,
        isArchived: project.status === 'archived',
        createdAt: new Date(project.created_at),
        hourlyRate: Number(project.hourly_rate),
        user_id: project.created_by,
        team_id: null,
        owner_id: project.created_by,
        is_team_project: true,
        
        isTeamProject: true
      }));

      return [...personalProjects, ...teamProjects].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }
  });


  // Filter projects and time entries by various criteria
  const filteredData = useMemo(() => {
    let filteredProjects = projects;
    let filteredTimeEntries = timeEntries;

    // Filter by billable status
    if (showBillableOnly) {
      filteredTimeEntries = filteredTimeEntries.filter(entry => entry.isBillable);
    }

    // Filter by team projects only
    if (showTeamProjectsOnly) {
      const teamProjectIds = filteredProjects.filter(p => p.isTeamProject).map(p => p.id);
      filteredTimeEntries = filteredTimeEntries.filter(entry => 
        entry.projectId && teamProjectIds.includes(entry.projectId)
      );
      filteredProjects = filteredProjects.filter(p => p.isTeamProject);
    }

    return { filteredProjects, filteredTimeEntries };
  }, [projects, timeEntries, showBillableOnly, showTeamProjectsOnly]);

  // Calculate revenue summary data with default rate for unassigned entries
  const revenueSummaryData = useMemo(() => {
    const { filteredTimeEntries, filteredProjects } = filteredData;
    const DEFAULT_HOURLY_RATE = 75; // Default rate for entries without projects
    
    let totalMinutes = 0;
    let billableMinutes = 0;
    let nonBillableMinutes = 0;
    let totalRevenue = 0;
    let billableRevenue = 0;
    let entriesWithoutProject = 0;

    filteredTimeEntries.forEach(entry => {
      const hours = entry.minutes / 60;
      totalMinutes += entry.minutes;

      // For team entries, use the hourly rate from the entry or project
      // For personal entries, get rate from project
      let hourlyRate = 0;
      if (entry.isTeamEntry && entry.hourlyRate) {
        hourlyRate = entry.hourlyRate;
      } else if (entry.projectId) {
        const project = filteredProjects.find(p => p.id === entry.projectId);
        hourlyRate = project?.hourlyRate || 0;
      } else {
        // Use default rate for unassigned entries
        hourlyRate = DEFAULT_HOURLY_RATE;
        entriesWithoutProject++;
      }

      const revenue = hours * hourlyRate;
      totalRevenue += revenue;

      if (entry.isBillable) {
        billableMinutes += entry.minutes;
        billableRevenue += revenue;
      } else {
        nonBillableMinutes += entry.minutes;
      }
    });

    const totalHours = totalMinutes / 60;
    const billableHours = billableMinutes / 60;
    const nonBillableHours = nonBillableMinutes / 60;
    const averageHourlyRate = billableHours > 0 ? billableRevenue / billableHours : 0;

    return {
      totalHours,
      billableHours,
      nonBillableHours,
      totalRevenue,
      billableRevenue,
      averageHourlyRate,
      entriesWithoutProject
    };
  }, [filteredData]);

  // Calculate project hours distribution for pie chart
  const projectHoursData = useMemo(() => {
    const { filteredTimeEntries, filteredProjects } = filteredData;
    
    if (!filteredTimeEntries.length || !filteredProjects.length) return [];
    
    const projectMinutes = filteredTimeEntries.reduce((acc, entry) => {
      if (!entry.projectId) return acc;
      acc[entry.projectId] = (acc[entry.projectId] || 0) + entry.minutes;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(projectMinutes)
      .filter(([projectId]) => {
        return filteredProjects.some(p => p.id === projectId);
      })
      .map(([projectId, minutes]) => {
        const project = filteredProjects.find(p => p.id === projectId);
        const hours = minutes / 60;
        const projectEntries = filteredTimeEntries.filter(e => e.projectId === projectId);
        
        // Calculate revenue from all entries for this project
        let projectRevenue = 0;
        projectEntries.forEach(entry => {
          const entryHours = entry.minutes / 60;
          const hourlyRate = entry.isTeamEntry && entry.hourlyRate ? 
            entry.hourlyRate : (project?.hourlyRate || 0);
          projectRevenue += entryHours * hourlyRate;
        });

        return {
          name: project?.name || 'Unknown Project',
          value: Math.round(hours * 10) / 10, // Round to 1 decimal
          id: projectId,
          hourlyRate: project?.hourlyRate || 0,
          revenue: projectRevenue,
          isTeamProject: project?.isTeamProject || false
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [filteredData]);

  // Calculate weekly hours data for bar chart
  const weeklyHoursData = useMemo(() => {
    const { filteredTimeEntries } = filteredData;
    
    if (!filteredTimeEntries.length) return [];
    
    const endDate = dateRange?.to || new Date();
    const startDate = dateRange?.from || new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayData = Array(7).fill(0).map((_, i) => ({
      name: weekDays[i],
      hours: 0
    }));
    
    filteredTimeEntries.forEach(entry => {
      if (entry.date >= startDate && entry.date <= endDate) {
        const dayOfWeek = entry.date.getDay();
        dayData[dayOfWeek].hours += entry.minutes / 60;
      }
    });
    
    return dayData.map(day => ({
      ...day,
      hours: parseFloat(day.hours.toFixed(1))
    }));
  }, [filteredData, dateRange]);

  const hasTimeEntries = timeEntries.length > 0;
  const hasProjects = projects.length > 0;
  const hasProjectData = projectHoursData.length > 0;

  return {
    timeEntries: filteredData.filteredTimeEntries,
    projects: filteredData.filteredProjects,
    projectHoursData,
    weeklyHoursData,
    revenueSummaryData,
    isLoading: isLoadingTimeEntries || isLoadingProjects,
    hasTimeEntries,
    hasProjects,
    hasProjectData
  };
}
